import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { SeatMapOverview, SeatMapSlotResource, SeatMapSlot } from '@src/core/buyerApiClient';
import { getDefaultAvailableArea } from '@src/common/seatMaps';
import SeatMapAreaSelector from './SeatMapAreaSelector';
import useTranslate from '../../i18n/useTranslate';
import Slot from './components/Slot';
import SeatMapLegendSelector from './SeatMapLegendSelector';

interface SeatMapCanvasProps {
  seatMap: SeatMapOverview;
  resourceStatus: (slot: SeatMapSlotResource) => string;
  onSelection: (res: SeatMapSlotResource) => void;
}

const BASE_MULTIPLIER = 0.75;

const SeatMapCanvas = ({ seatMap, resourceStatus, onSelection }: SeatMapCanvasProps) => {
  const [sizeMultiplier, setSizeMultiplier] = useState(BASE_MULTIPLIER);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(
    () => getDefaultAvailableArea(seatMap.areas, resourceStatus)?.id ?? null,
  );
  const parentRef = useRef<HTMLDivElement>(null);
  const { i18nElement } = useTranslate();

  const selectedArea = useMemo(
    () => seatMap.areas.find((a) => a.id === selectedAreaId),
    [selectedAreaId, seatMap.areas],
  );

  useEffect(() => {
    const refreshSizeMultiplier = () => {
      if (selectedArea && parentRef.current) {
        const mult = parentRef.current.offsetWidth / selectedArea.width;
        setSizeMultiplier(Math.min(mult, BASE_MULTIPLIER));
      }
    };

    refreshSizeMultiplier();

    window.addEventListener('resize', refreshSizeMultiplier);
    return () => window.removeEventListener('resize', refreshSizeMultiplier);
  }, []);

  const widthAreaCss = selectedArea && { width: '100%', maxWidth: Math.round(selectedArea.width * sizeMultiplier) };

  const areaCss = selectedArea
    ? {
        ...widthAreaCss,
        height: Math.round(selectedArea.height * sizeMultiplier),
        backgroundImage: `url(${selectedArea.bgImageUrl})`,
        backgroundSize: 'cover',
      }
    : {};
  if (selectedArea == null) {
    return <span>{i18nElement('no_option_found')}</span>;
  }

  const getSlotCategory = (slot: SeatMapSlot) =>
    seatMap.categories.find((category) => category.id === slot.resource.categoryId);

  return (
    <Canvas>
      <InteractionArea style={widthAreaCss ?? undefined}>
        <SeatMapLegendSelector />

        {seatMap.areas.length > 1 ? (
          <SeatMapAreaSelector
            areas={seatMap.areas}
            selectedAreaId={selectedAreaId}
            onChange={(areaId) => setSelectedAreaId(areaId)}
          />
        ) : null}
      </InteractionArea>

      <CanvasHolder ref={parentRef}>
        <SlotHolder style={areaCss}>
          {selectedArea!.slots.map((slot) => (
            <Slot
              key={slot.id}
              slot={slot}
              sizeMultiplier={sizeMultiplier}
              onClick={onSelection}
              status={resourceStatus(slot.resource!)}
              category={getSlotCategory(slot)!}
            />
          ))}
        </SlotHolder>
      </CanvasHolder>
    </Canvas>
  );
};

const Canvas = styled.div``;
const CanvasHolder = styled.div`
  position: relative;
`;

const InteractionArea = styled.div`
  display: flex;
  margin: auto;
  margin-bottom: 8px;
  align-items: center;
  justify-content: space-between;
`;

const SlotHolder = styled.div`
  position: relative;
  margin: auto;
  max-width: 100%;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.15);
`;

export default SeatMapCanvas;
